import * as v from 'valibot'

export const defaultEntitySearch = {
	skip: 0,
	take: 50,
} as const

type WithPagination = {
	pagination: {
		pageIndex: number
		pageSize: number
	}
}

export const addPagination = <T extends { skip: number; take: number }>(
	search: T,
): WithPagination => ({
	pagination: {
		pageIndex: Math.floor(search.skip / search.take),
		pageSize: search.take,
	},
})

export const EntitySearchSchema = v.object({
	skip: v.pipe(v.fallback(v.number(), defaultEntitySearch.skip), v.minValue(0)),
	take: v.pipe(v.fallback(v.number(), defaultEntitySearch.take), v.minValue(0)),
})

export type EntitySearch = v.InferOutput<typeof EntitySearchSchema>

export const EntitySearchWithPaginationSchema = v.pipe(
	EntitySearchSchema,
	v.transform((search) => ({
		...search,
		...addPagination(search),
	})),
)

export type EntitySearchWithPagination = v.InferOutput<
	typeof EntitySearchWithPaginationSchema
>

type WithFilters = Partial<Record<string, string | number | boolean>>

export function addFilters<
	T extends {
		skip: number
		take: number
		sortColumn?: string
		sortDirection?: number
	} & Partial<WithFilters>,
>(search: T): WithFilters {
	const { skip, take, sortColumn, sortDirection, ...filters } = search
	return filters
}

export const EntitysListSearchSchema = v.pipe(
	v.objectWithRest(
		{
			...EntitySearchSchema.entries,
			sortColumn: v.optional(v.string()),
			sortDirection: v.optional(v.number()),
		},
		v.union([v.string(), v.number(), v.boolean()]),
	),
)

export const EntitysListSearchWithFiltersAndPaginationSchema = v.pipe(
	EntitysListSearchSchema,
	v.transform((search) => ({
		...search,
		...addPagination(search),
		...addFilters(search),
	})),
)

export type EntitysListSearch = v.InferOutput<typeof EntitysListSearchSchema>
export type EntitysListSearchWithFiltersAndPagination = v.InferOutput<
	typeof EntitysListSearchWithFiltersAndPaginationSchema
>
